import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/api'

interface AuthGuardProps {
  children: ReactNode
  requiredRole?: 'admin_staff' | 'admin' | 'editor' | 'viewer'
}

const AuthGuard = ({ children, requiredRole = 'viewer' }: AuthGuardProps) => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C5282F]"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const roleHierarchy: Record<string, number> = {
    viewer: 0,
    editor: 1,
    admin: 2,
    admin_staff: 2,
    VIEWER: 0,
    EDITOR: 1,
    ADMIN: 2,
    ADMIN_STAFF: 2,
    // Handle common API role variations
    USER: 0,
    MODERATOR: 1,
    ADMINISTRATOR: 2
  }

  const userRole = user?.userRole?.toLowerCase() || 'viewer'
  const userRoleLevel = roleHierarchy[user?.userRole || 'viewer'] ?? roleHierarchy[userRole] ?? 0
  const requiredRoleLevel = roleHierarchy[requiredRole] ?? 0

  if (userRoleLevel < requiredRoleLevel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this resource.
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-[#C5282F] text-white px-6 py-2 rounded-lg hover:bg-[#A02228] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default AuthGuard